import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../../utils/toast";
import { FightItem, FormErrors, UpdateProfileUIProps } from "../../../types/updateProfileProps";
import { ContriesDropdown } from "../../common/countriesDropdown";
import { FigmaDatePicker } from "./basicInfo";
import {
    AddPill,
    Card,
    FieldError,
    FieldLabel,
    FormActions,
    SectionTitle,
    SelectField,
    TextField,
    getDateLanguage,
} from "./formPrimitives";

/** Backend contract: FightHistoryDto typeOfEnding @Pattern + preferredRuleSet values */
const ENDINGS = ["KO", "TKO", "SUBMISSION", "DECISION", "DRAW", "DQ", "NO_CONTEST"];
/** "AMETEUR" spelling is the real backend value */
const LEVELS = ["AMETEUR", "SEMI_PRO", "PROFESSIONAL"];
const RESULTS = ["Win", "Loss", "Draw"];

const OPPONENT_MAX_LENGTH = 30;
const ORGANIZATION_MAX_LENGTH = 40;

type Draft = {
    opponentName: string;
    date: string;
    result: string;
    round: string;
    typeOfEnding: string;
    location: string;
    organizationName: string;
    preferredRuleSet: string;
};

type Mode = "idle" | "add" | number;

const EMPTY_DRAFT: Draft = {
    opponentName: "",
    date: "",
    result: "",
    round: "",
    typeOfEnding: "",
    location: "",
    organizationName: "",
    preferredRuleSet: "",
};

/** Backend sends "WIN", the form uses "Win" */
const getResultKey = (result: string) => {
    const match = RESULTS.find((key) => key.toLowerCase() === result.toLowerCase());
    return match ?? result;
};

const toDraft = (fight: FightItem): Draft => ({
    opponentName: fight.opponentName,
    date: fight.date,
    result: getResultKey(fight.result),
    round: String(fight.round ?? ""),
    typeOfEnding: fight.typeOfEnding,
    location: fight.location,
    organizationName: fight.organizationName ?? "",
    preferredRuleSet: fight.preferredRuleSet,
});

/** Parse yyyy-MM-dd as a LOCAL date (new Date("2026-08-05") would shift by time zone) */
const formatFightDate = (isoDate: string, language: string) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate);
    if (!match) return isoDate || "-";

    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return date.toLocaleDateString(getDateLanguage(language), { month: "short", day: "numeric", year: "numeric" });
};

const getBadgeClass = (result: string) => {
    const value = result.toLowerCase();
    if (value === "win") return "bg-[#DCFCE7]";
    if (value === "loss") return "bg-[#FEE2E2]";
    return "bg-[#FEF3C7]";
};

const ReadField: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
    <div className="flex min-w-0 flex-col">
        <span className="text-sm leading-5 text-gray-500">{label}</span>
        <span className="break-words text-base font-bold leading-6 text-gray-900">{value || "-"}</span>
    </div>
);

const FightHistorySection: React.FC<UpdateProfileUIProps> = ({ fights, commitFights, countries, isPersisting }) => {
    const { t, i18n } = useTranslation("updateProfile");
    const [mode, setMode] = useState<Mode>("idle");
    const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
    const [errors, setErrors] = useState<FormErrors>({});

    const updateDraft = (key: keyof Draft, value: string) => {
        setDraft((previous) => ({ ...previous, [key]: value }));
        setErrors((previous) => ({ ...previous, [key]: "" }));
    };

    const openForm = (nextMode: "add" | number) => {
        setDraft(nextMode === "add" ? EMPTY_DRAFT : toDraft(fights[nextMode]));
        setErrors({});
        setMode(nextMode);
    };

    const closeForm = () => {
        setMode("idle");
        setDraft(EMPTY_DRAFT);
        setErrors({});
    };

    const validate = (): boolean => {
        const formErrors: FormErrors = {};
        const requiredMessage = t("page.errors.required");

        if (!draft.opponentName.trim()) {
            formErrors.opponentName = requiredMessage;
        } else if (draft.opponentName.length > OPPONENT_MAX_LENGTH) {
            formErrors.opponentName = t("page.errors.maxLength", { max: OPPONENT_MAX_LENGTH });
        }

        if (!draft.date) formErrors.date = requiredMessage;
        if (!draft.result) formErrors.result = requiredMessage;
        if (!draft.round || Number(draft.round) < 1) formErrors.round = t("page.errors.roundMin");
        if (!draft.typeOfEnding) formErrors.typeOfEnding = requiredMessage;
        if (!draft.location.trim()) formErrors.location = requiredMessage;
        if (!draft.preferredRuleSet) formErrors.preferredRuleSet = requiredMessage;

        if (draft.organizationName.length > ORGANIZATION_MAX_LENGTH) {
            formErrors.organizationName = t("page.errors.maxLength", { max: ORGANIZATION_MAX_LENGTH });
        }

        setErrors(formErrors);
        return Object.keys(formErrors).length === 0;
    };

    const submit = async () => {
        if (!validate()) return;

        const fight: FightItem = {
            opponentName: draft.opponentName.trim(),
            date: draft.date,
            location: draft.location.trim(),
            result: draft.result,
            round: Number(draft.round),
            typeOfEnding: draft.typeOfEnding,
            organizationName: draft.organizationName.trim() || undefined,
            preferredRuleSet: draft.preferredRuleSet,
        };

        const nextFights =
            mode === "add" ? [...fights, fight] : fights.map((item, index) => (index === mode ? fight : item));

        const isSaved = await commitFights(nextFights);
        if (isSaved) closeForm();
    };

    const removeFight = async (index: number) => {
        // Backend requires at least one fight (FighterDto.fightHistoryDetailsDto is @NotEmpty)
        if (fights.length <= 1) {
            toast.error(t("page.errors.lastFight"));
            return;
        }

        await commitFights(fights.filter((_, itemIndex) => itemIndex !== index));
    };

    const renderForm = (title: string, confirmLabel: string) => (
        <Card className="flex flex-col gap-3">
            <p className="text-sm font-medium leading-5 text-gray-900">{title}</p>
            <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                <TextField
                    name="fightOpponent"
                    label={t("page.opponentName")}
                    value={draft.opponentName}
                    maxLength={OPPONENT_MAX_LENGTH}
                    placeholder={t("page.enterOpponentName")}
                    onValueChange={(value) => updateDraft("opponentName", value)}
                    error={errors.opponentName}
                />
                <FigmaDatePicker
                    name="fightDate"
                    label={t("page.date")}
                    maxToday
                    value={draft.date}
                    onValueChange={(value) => updateDraft("date", value)}
                    error={errors.date}
                />
                <SelectField
                    name="fightResult"
                    label={t("page.result")}
                    value={draft.result}
                    placeholder={t("page.selectResult")}
                    options={RESULTS.map((result) => ({ value: result, label: t(`page.results.${result}`) }))}
                    onValueChange={(value) => updateDraft("result", value)}
                    error={errors.result}
                />
                <TextField
                    name="fightRound"
                    label={t("page.round")}
                    inputMode="numeric"
                    maxLength={2}
                    value={draft.round}
                    placeholder="0"
                    onValueChange={(value) => updateDraft("round", value.replace(/\D/g, ""))}
                    error={errors.round}
                />
                <SelectField
                    name="fightMethod"
                    label={t("page.method")}
                    value={draft.typeOfEnding}
                    placeholder={t("page.selectMethod")}
                    options={ENDINGS.map((ending) => ({ value: ending, label: t(`page.endings.${ending}`) }))}
                    onValueChange={(value) => updateDraft("typeOfEnding", value)}
                    error={errors.typeOfEnding}
                />
                <div className="flex w-full flex-col gap-1">
                    <FieldLabel>{t("page.location")}</FieldLabel>
                    <ContriesDropdown
                        variant="figma"
                        strict
                        options={countries}
                        value={draft.location}
                        placeholder={t("page.selectLocation")}
                        hasError={!!errors.location}
                        onLocationChange={(value) => updateDraft("location", value)}
                    />
                    <FieldError message={errors.location} />
                </div>
                <TextField
                    name="fightOrganization"
                    label={t("page.organizationName")}
                    value={draft.organizationName}
                    maxLength={ORGANIZATION_MAX_LENGTH}
                    placeholder={t("page.enterOrganizationName")}
                    onValueChange={(value) => updateDraft("organizationName", value)}
                    error={errors.organizationName}
                />
                <SelectField
                    name="fightLevel"
                    label={t("page.competitionLevel")}
                    value={draft.preferredRuleSet}
                    placeholder={t("page.selectLevel")}
                    options={LEVELS.map((level) => ({ value: level, label: t(`page.levels.${level}`) }))}
                    onValueChange={(value) => updateDraft("preferredRuleSet", value)}
                    error={errors.preferredRuleSet}
                />
            </div>
            <FormActions
                cancelLabel={t("page.cancel")}
                confirmLabel={confirmLabel}
                busy={isPersisting}
                onCancel={closeForm}
                onConfirm={submit}
            />
        </Card>
    );

    return (
        <section id="section-fight-history" className="flex flex-col gap-4">
            <SectionTitle>{t("page.fightHistory")}</SectionTitle>

            {mode === "idle" && (
                <AddPill onClick={() => openForm("add")} disabled={isPersisting}>
                    {t("page.addFightHistory")}
                </AddPill>
            )}

            {fights.map((fight, index) => {
                // the React.Fragment is required: it carries the list key for the edit form
                if (mode === index) {
                    return (
                        <React.Fragment key={`edit-${index}`}>
                            {renderForm(t("page.fightN", { n: index + 1 }), t("page.updateFight"))}
                        </React.Fragment>
                    );
                }

                return (
                    <Card key={`${index}-${fight.opponentName}-${fight.date}`} className="flex flex-col gap-3">
                        <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-medium leading-5 text-gray-900">
                                {t("page.fightN", { n: index + 1 })}
                            </p>
                            <div className="flex items-center gap-2">
                                <span
                                    className={`rounded-full px-2 py-0.5 text-xs font-medium text-gray-900 ${getBadgeClass(
                                        fight.result
                                    )}`}
                                >
                                    {t(`page.results.${getResultKey(fight.result)}`, { defaultValue: fight.result })}
                                </span>
                                <button
                                    type="button"
                                    aria-label={t("page.edit")}
                                    disabled={isPersisting}
                                    onClick={() => openForm(index)}
                                    className="text-gray-900 transition-colors hover:text-blue-600 disabled:opacity-50"
                                >
                                    <PencilIcon className="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    aria-label={t("page.remove")}
                                    disabled={isPersisting}
                                    onClick={() => removeFight(index)}
                                    className="text-gray-900 transition-colors hover:text-red-600 disabled:opacity-50"
                                >
                                    <TrashIcon className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                            <ReadField label={t("page.opponentName")} value={fight.opponentName} />
                            <ReadField label={t("page.date")} value={formatFightDate(fight.date, i18n.language)} />
                            <ReadField
                                label={t("page.method")}
                                value={t(`page.endings.${fight.typeOfEnding}`, { defaultValue: fight.typeOfEnding })}
                            />
                            <ReadField label={t("page.location")} value={fight.location} />
                            <ReadField label={t("page.organization")} value={fight.organizationName} />
                            <ReadField
                                label={t("page.competitionLevel")}
                                value={t(`page.levels.${fight.preferredRuleSet}`, {
                                    defaultValue: fight.preferredRuleSet,
                                })}
                            />
                        </div>
                    </Card>
                );
            })}

            {mode === "add" && renderForm(t("page.fightN", { n: fights.length + 1 }), t("page.addFight"))}
        </section>
    );
};

export default FightHistorySection;
